import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExploreIcon from '@mui/icons-material/Explore';
import HubIcon from '@mui/icons-material/Hub';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Box, Card, CardActionArea, CircularProgress, Stack, Typography } from '@mui/material';

export type SuggestionItem = {
  label: string;
  prompt: string;
  actionKey?:
    | 'discover_components'
    | 'list_datasources'
    | 'list_agents'
    | 'list_user_apps'
    | 'list_pipelines'
    | 'list_blueprints'
    | 'pipeline_status';
};

type Props = {
  onSelectSuggestion?: (item: SuggestionItem) => void;
  onSelectPrompt?: (promptText: string) => void;
};

const SUGGESTIONS: { category: string; icon: React.ReactElement; items: SuggestionItem[] }[] = [
  {
    category: 'Component Discovery',
    icon: <ExploreIcon sx={{ fontSize: 18, color: '#0284c7' }} />,
    items: [
      {
        label: 'What data sources and agents do I have available?',
        prompt: 'What data sources and agents do I have available?',
        actionKey: 'discover_components',
      },
      {
        label: 'Show all data source instances',
        prompt: 'show data source instances',
        actionKey: 'list_datasources',
      },
      {
        label: 'List all agents and implementation stacks',
        prompt: 'list all agents',
        actionKey: 'list_agents',
      },
      {
        label: 'List all user applications',
        prompt: 'list all user apps instances',
        actionKey: 'list_user_apps',
      },
      {
        label: 'Show all pipeline instances',
        prompt: 'show pipelines',
        actionKey: 'list_pipelines',
      },
    ],
  },
  {
    category: 'Pipelines & Blueprints',
    icon: <HubIcon sx={{ fontSize: 18, color: '#10b981' }} />,
    items: [
      {
        label: 'What pipeline blueprints are available?',
        prompt: 'What pipeline blueprints are available?',
        actionKey: 'list_blueprints',
      },
      {
        label: 'Create a research paper analysis pipeline',
        prompt: 'Help me build a research paper analysis pipeline',
      },
      {
        label: 'Create pipeline from blueprint',
        prompt: 'List pipeline blueprints and help me instantiate one',
        actionKey: 'list_blueprints',
      },
    ],
  },
  {
    category: 'Status & Execution',
    icon: <PlayCircleOutlineIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />,
    items: [
      {
        label: 'Show data source processing status',
        prompt: 'Show processing status of data sources',
        actionKey: 'list_datasources',
      },
      {
        label: 'Check pipeline execution status',
        prompt: 'Check execution plan and status of pipelines',
        actionKey: 'pipeline_status',
      },
    ],
  },
];

const EmptyContextPanel = ({ onSelectSuggestion, onSelectPrompt }: Props) => {
  const [loadingLabel, setLoadingLabel] = React.useState<string | null>(null);

  const handleClick = (item: SuggestionItem) => {
    if (loadingLabel) return;
    setLoadingLabel(item.label);
    if (onSelectSuggestion) {
      onSelectSuggestion(item);
    } else if (onSelectPrompt) {
      onSelectPrompt(item.prompt);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2.5, bgcolor: '#f8fafc' }}>
      <Stack spacing={2.5}>
        {SUGGESTIONS.map((cat) => (
          <Box key={cat.category}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              {cat.icon}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                {cat.category}
              </Typography>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              {cat.items.map((item) => {
                const isLoading = loadingLabel === item.label;
                return (
                  <Card
                    key={item.label}
                    variant="outlined"
                    sx={{
                      borderRadius: 1.5,
                      borderColor: isLoading ? '#3b82f6' : '#e2e8f0',
                      bgcolor: isLoading ? '#f0f9ff' : '#ffffff',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleClick(item)}
                      disabled={loadingLabel !== null && !isLoading}
                      sx={{
                        p: 1.5,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        {isLoading ? (
                          <CircularProgress size={16} thickness={5} sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                        ) : (
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#64748b', mt: 0.25, flexShrink: 0 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isLoading ? 'primary.main' : '#1e293b',
                            fontSize: '0.8rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Stack>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default EmptyContextPanel;
